import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { api, tokenSpeicher, type BenutzerAntwort } from '../api/client'

interface AuthWert {
  benutzer: BenutzerAntwort | null
  laedt: boolean
  anmelden: (email: string, passwort: string) => Promise<void>
  abmelden: () => void
  hatRolle: (...rollen: string[]) => boolean
}

const AuthKontext = createContext<AuthWert | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [benutzer, setBenutzer] = useState<BenutzerAntwort | null>(null)
  const [laedt, setLaedt] = useState(true)

  // Beim Laden der Seite prüfen, ob ein gespeichertes Token noch trägt.
  useEffect(() => {
    let abgebrochen = false
    async function pruefe() {
      if (!tokenSpeicher.access()) {
        setLaedt(false)
        return
      }
      try {
        const ich = await api.get<BenutzerAntwort>('/api/auth/ich')
        if (!abgebrochen) setBenutzer(ich)
      } catch {
        tokenSpeicher.leeren()
      } finally {
        if (!abgebrochen) setLaedt(false)
      }
    }
    void pruefe()
    return () => {
      abgebrochen = true
    }
  }, [])

  // Der Client meldet abgelaufene Sitzungen über ein Ereignis.
  useEffect(() => {
    const beiAbmeldung = () => setBenutzer(null)
    window.addEventListener('vlzhub:abgemeldet', beiAbmeldung)
    return () => window.removeEventListener('vlzhub:abgemeldet', beiAbmeldung)
  }, [])

  const anmelden = useCallback(async (email: string, passwort: string) => {
    const antwort = await api.anmelden(email, passwort)
    setBenutzer(antwort.benutzer)
  }, [])

  const abmelden = useCallback(() => {
    tokenSpeicher.leeren()
    setBenutzer(null)
  }, [])

  const hatRolle = useCallback(
    (...rollen: string[]) => rollen.some((r) => benutzer?.rollen.includes(r)),
    [benutzer],
  )

  const wert = useMemo(
    () => ({ benutzer, laedt, anmelden, abmelden, hatRolle }),
    [benutzer, laedt, anmelden, abmelden, hatRolle],
  )

  return <AuthKontext.Provider value={wert}>{children}</AuthKontext.Provider>
}

export function useAuth(): AuthWert {
  const kontext = useContext(AuthKontext)
  if (!kontext) throw new Error('useAuth muss innerhalb von AuthProvider verwendet werden')
  return kontext
}
